'use client';
import { useCallback, useContext, useEffect, useRef } from 'react';
import { SessionContext } from '@benzinga/session-context';
import { TrackingManager } from '@benzinga/tracking-manager';
import { trailingThrottle } from '@benzinga/utils';

/**
 * Fires `scroll` tracking events at 25/50/75/100% depth.
 *
 * @param extraProperties - Optional properties merged into every scroll event.
 *   Pass `{ page_section }` so per-page Segment funnels can attribute scroll
 *   milestones to a specific route. Read through a ref so callers can pass an
 *   inline object without re-subscribing the scroll listener on every render.
 */
export const useScrollDepthTracking = (extraProperties?: Record<string, unknown>) => {
  const session = useContext(SessionContext);
  const extraPropertiesRef = useRef(extraProperties);
  extraPropertiesRef.current = extraProperties;
  const scrollDepths = useRef({
    25: false,
    50: false,
    75: false,
    // eslint-disable-next-line sort-keys-fix/sort-keys-fix
    100: false,
  });

  const sendScrollEvent = useCallback(depth => {
    session.getManager(TrackingManager).trackPageEvent('scroll', {
      ...extraPropertiesRef.current,
      scroll_depth: parseInt(depth),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleScroll = useCallback(() => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
    const scrollPercentage = Math.round((scrollTop / scrollHeight) * 100);

    Object.keys(scrollDepths.current).forEach(depth => {
      if (!scrollDepths.current[depth] && scrollPercentage >= parseInt(depth)) {
        scrollDepths.current[depth] = true;
        sendScrollEvent(depth);
      }
    });
  }, [sendScrollEvent]);

  useEffect(() => {
    const throttledHandleScroll = trailingThrottle(handleScroll, 400);
    window.addEventListener('scroll', throttledHandleScroll);
    return () => window.removeEventListener('scroll', throttledHandleScroll);
  }, [handleScroll]);
};

export default useScrollDepthTracking;
